/* eslint-disable max-lines */

import { joinByDelimiter } from '../utils';

export enum Permission {
  AddressManagement = 'address_management',
  Administration = 'administration',
  AllUsers = 'all_users',
  AllocationViews = 'allocation_views',
  AllocationViewProfiles = 'allocation_view_profiles',
  ApiConfiguration = 'api_configuration',
  Approve = 'approve',
  AutomotiveAftermarket = 'automotive_aftermarket',
  CanaryTransportLevel = 'canary_transport_level',
  Compartments = 'compartments',
  ContainerManualUpload = 'container_manual_upload',
  Create = 'create',
  DaliView = 'dali_view',
  DataProtectionNotice = 'data_protection_notice',
  DataQuality = 'data_quality',
  DataQualityExports = 'data_quality_exports',
  Delete = 'delete',
  DeliveryNote = 'delivery_note',
  DeliveryNoteCard = 'delivery_note_card',
  DockAndYard = 'DY',
  Docks = 'docks',
  Drivers = 'drivers',
  EndUserLicenseAgreement = 'end_user_license_agreement',
  Exports = 'exports',
  AdminExports = 'admin_exports',
  AddressExport = 'address_export',
  PartnerExports = 'partner_exports',
  UserExports = 'user_exports',
  TTExports = 'tt_exports',
  ShipmentExport = 'shipment_export',
  FailedSc4IFTSTA = 'failed_sc4_iftsta',
  HandlingUnit = 'handling_unit',
  HandlingUnitCard = 'handling_unit_card',
  Hubs = 'hubs',
  Load = 'load',
  LoanerPhone = 'loaner_phone',
  LocationManagement = 'location_management',
  MasterData = 'master_data',
  NeedApprove = 'need_approve',
  Newsletters = 'newsletters',
  Organizations = 'organizations',
  PartnerManagement = 'partner_management',
  PodManagement = 'pod_management',
  PickUpPoint = 'pick_up_point',
  PlantManagement = 'plant_management',
  Read = 'read',
  Reports = 'reports',
  SapEtaUp = 'sap_eta_up',
  SecurityCheck = 'security_check',
  Self = 'self',
  ShipmentComments = 'shipment_comments',
  ShipmentCustoms = 'shipment_customs',
  Shipper = 'shipper',
  SupportInfo = 'support_info',
  TerminalView = 'terminal_view',
  TerminalViewSettings = 'terminal_view_settings',
  CalendarView = 'calendar_view',
  CalendarViewSettings = 'calendar_view_settings',
  DriverView = 'driver_view',
  DriverViewSettings = 'driver_view_settings',
  TileType = 'tile_type',
  TmSc1Sc4 = 'tm_sc1_sc4',
  TpComments = 'tp_comments',
  TpCustoms = 'tp_customs',
  TrackAndTrace = 'TT',
  TransportOrder = 'transport_order',
  TruckDetails = 'truck_details',
  TruckMapping = 'truck_mapping',
  TransportContent = 'transport_content',
  UliDetails = 'uli_details',
  UliOverview = 'uli_overview',
  Update = 'update',
  UserDataChanges = 'user_data_changes',
  UserManagement = 'user_management',
  GateAddressIdManagement = 'gate_address_id_management',
  Export = 'export',
  Users = 'users',
  ParkingSpots = 'parking_spots',
}

interface ItemCrudPermissions {
  [Permission.Self]: string;
  [Permission.Create]: string;
  [Permission.Read]: string;
  [Permission.Update]: string;
  [Permission.Delete]: string;
}

export interface PermissionConfig {
  [Permission.AllUsers]: {
    [Permission.Self]: string;
  };
  [Permission.DockAndYard]: {
    [Permission.Self]: string;
    [Permission.LocationManagement]: {
      [Permission.Self]: string;
      [Permission.Hubs]: ItemCrudPermissions;
      [Permission.Compartments]: ItemCrudPermissions;
      [Permission.Docks]: ItemCrudPermissions;
      [Permission.ParkingSpots]: ItemCrudPermissions;
      [Permission.SecurityCheck]: {
        [Permission.Self]: string;
      };
      [Permission.LoanerPhone]: {
        [Permission.Self]: string;
      };
      [Permission.Drivers]: {
        [Permission.Self]: string;
      };
      [Permission.TruckMapping]: {
        [Permission.Self]: string;
      };
    };
    [Permission.AllocationViews]: {
      [Permission.Self]: string;
      [Permission.UliOverview]: {
        [Permission.Self]: string;
        [Permission.Read]: string;
        [Permission.UliDetails]: {
          [Permission.Self]: string;
          [Permission.Read]: string;
          [Permission.Update]: string;
        };
        [Permission.TruckDetails]: {
          [Permission.Self]: string;
          [Permission.Read]: string;
          [Permission.Update]: string;
        };
      };
      [Permission.TerminalView]: {
        [Permission.Self]: string;
      };
      [Permission.CalendarView]: {
        [Permission.Self]: string;
      };
      [Permission.DriverView]: {
        [Permission.Self]: string;
      };
      [Permission.AllocationViewProfiles]: {
        [Permission.Self]: string;
        [Permission.CalendarViewSettings]: {
          [Permission.Self]: string;
        };
        [Permission.TerminalViewSettings]: {
          [Permission.Self]: string;
        };
        [Permission.DriverViewSettings]: {
          [Permission.Self]: string;
        };
      };
    };

    [Permission.DaliView]: {
      [Permission.Self]: string;
    };
  };
  [Permission.Administration]: {
    [Permission.Self]: string;
    [Permission.UserManagement]: {
      [Permission.Self]: string;
      [Permission.Drivers]: {
        [Permission.Self]: string;
      };
      [Permission.Users]: ItemCrudPermissions & {
        [Permission.Approve]: string;
      };
    };
    [Permission.PartnerManagement]: ItemCrudPermissions & {
      [Permission.Approve]: string;
    };
    [Permission.PlantManagement]: ItemCrudPermissions & {
      [Permission.Self]: string;
    };
    [Permission.ApiConfiguration]: {
      [Permission.Self]: string;
    };
    [Permission.AddressManagement]: ItemCrudPermissions & {
      [Permission.GateAddressIdManagement]: {
        [Permission.Create]: string;
        [Permission.Read]: string;
      };
      [Permission.Export]: string;
    };
    [Permission.Organizations]: {
      [Permission.Self]: string;
    };
    [Permission.DataProtectionNotice]: {
      [Permission.Self]: string;
    };
    [Permission.EndUserLicenseAgreement]: {
      [Permission.Self]: string;
    };
    [Permission.Newsletters]: ItemCrudPermissions;
    [Permission.SapEtaUp]: {
      [Permission.Self]: string;
    };
    [Permission.ContainerManualUpload]: {
      [Permission.Self]: string;
    };
  };
  [Permission.Reports]: {
    [Permission.Self]: string;
    [Permission.UserDataChanges]: {
      [Permission.Self]: string;
    };
    [Permission.MasterData]: {
      [Permission.Self]: string;
    };
  };
  [Permission.TrackAndTrace]: {
    [Permission.Self]: string;
    [Permission.AutomotiveAftermarket]: {
      [Permission.Self]: string;
      [Permission.DeliveryNoteCard]: {
        [Permission.Self]: string;
        [Permission.PickUpPoint]: string;
        [Permission.Shipper]: string;
      };
      [Permission.DeliveryNote]: {
        [Permission.PodManagement]: {
          [Permission.Self]: string;
          [Permission.Create]: string;
          [Permission.Read]: string;
          [Permission.Update]: string;
          [Permission.Delete]: string;
        };
      };
      [Permission.HandlingUnitCard]: {
        [Permission.Self]: string;
        [Permission.PickUpPoint]: string;
        [Permission.Shipper]: string;
      };
      [Permission.TileType]: {
        [Permission.Self]: string;
        [Permission.Load]: string;
        [Permission.TransportOrder]: string;
        [Permission.DeliveryNote]: string;
        [Permission.HandlingUnit]: string;
      };
    };
    [Permission.TmSc1Sc4]: {
      [Permission.Self]: string;
      [Permission.TpComments]: {
        [Permission.Self]: string;
        [Permission.Read]: string;
        [Permission.Create]: string;
      };
      [Permission.TpCustoms]: {
        [Permission.Self]: string;
        [Permission.Read]: string;
      };
    };
    [Permission.CanaryTransportLevel]: {
      [Permission.Self]: string;
      [Permission.ShipmentCustoms]: {
        [Permission.Self]: string;
        [Permission.Read]: string;
      };
      [Permission.ShipmentComments]: {
        [Permission.Self]: string;
        [Permission.Read]: string;
        [Permission.Create]: string;
      };
    };
    [Permission.TransportContent]: {
      [Permission.Self]: string;
    };
  };
  [Permission.Exports]: {
    [Permission.Self]: string;
    [Permission.TTExports]: {
      [Permission.Self]: string;
      [Permission.ShipmentExport]: {
        [Permission.Self]: string;
      };
    };
    [Permission.DataQualityExports]: {
      [Permission.Self]: string;
    };
    [Permission.AdminExports]: {
      [Permission.Self]: string;
      [Permission.AddressExport]: {
        [Permission.Self]: string;
      };
      [Permission.PartnerExports]: {
        [Permission.Self]: string;
      };
      [Permission.UserExports]: {
        [Permission.Self]: string;
      };
    };
  };
  [Permission.DataQuality]: {
    [Permission.Self]: string;
    [Permission.FailedSc4IFTSTA]: {
      [Permission.Self]: string;
      [Permission.Read]: string;
    };
  };
  [Permission.SupportInfo]: {
    [Permission.Self]: string;
  };
}

export const PERMISSION_CONFIG: PermissionConfig = {
  [Permission.AllUsers]: {
    [Permission.Self]: Permission.AllUsers,
  },
  [Permission.DockAndYard]: {
    [Permission.Self]: Permission.DockAndYard,
    [Permission.LocationManagement]: {
      [Permission.Self]: joinByDelimiter<Permission>([Permission.DockAndYard, Permission.LocationManagement]),
      [Permission.Hubs]: {
        [Permission.Self]: joinByDelimiter<Permission>([
          Permission.DockAndYard,
          Permission.LocationManagement,
          Permission.Hubs,
        ]),
        [Permission.Create]: joinByDelimiter<Permission>([
          Permission.DockAndYard,
          Permission.LocationManagement,
          Permission.Hubs,
          Permission.Create,
        ]),
        [Permission.Read]: joinByDelimiter<Permission>([
          Permission.DockAndYard,
          Permission.LocationManagement,
          Permission.Hubs,
          Permission.Read,
        ]),
        [Permission.Update]: joinByDelimiter<Permission>([
          Permission.DockAndYard,
          Permission.LocationManagement,
          Permission.Hubs,
          Permission.Update,
        ]),
        [Permission.Delete]: joinByDelimiter<Permission>([
          Permission.DockAndYard,
          Permission.LocationManagement,
          Permission.Hubs,
          Permission.Delete,
        ]),
      },
      [Permission.Compartments]: {
        [Permission.Self]: joinByDelimiter<Permission>([
          Permission.DockAndYard,
          Permission.LocationManagement,
          Permission.Compartments,
        ]),
        [Permission.Create]: joinByDelimiter<Permission>([
          Permission.DockAndYard,
          Permission.LocationManagement,
          Permission.Compartments,
          Permission.Create,
        ]),
        [Permission.Read]: joinByDelimiter<Permission>([
          Permission.DockAndYard,
          Permission.LocationManagement,
          Permission.Compartments,
          Permission.Read,
        ]),
        [Permission.Update]: joinByDelimiter<Permission>([
          Permission.DockAndYard,
          Permission.LocationManagement,
          Permission.Compartments,
          Permission.Update,
        ]),
        [Permission.Delete]: joinByDelimiter<Permission>([
          Permission.DockAndYard,
          Permission.LocationManagement,
          Permission.Compartments,
          Permission.Delete,
        ]),
      },
      [Permission.Docks]: {
        [Permission.Self]: joinByDelimiter<Permission>([
          Permission.DockAndYard,
          Permission.LocationManagement,
          Permission.Docks,
        ]),
        [Permission.Create]: joinByDelimiter<Permission>([
          Permission.DockAndYard,
          Permission.LocationManagement,
          Permission.Docks,
          Permission.Create,
        ]),
        [Permission.Read]: joinByDelimiter<Permission>([
          Permission.DockAndYard,
          Permission.LocationManagement,
          Permission.Docks,
          Permission.Read,
        ]),
        [Permission.Update]: joinByDelimiter<Permission>([
          Permission.DockAndYard,
          Permission.LocationManagement,
          Permission.Docks,
          Permission.Update,
        ]),
        [Permission.Delete]: joinByDelimiter<Permission>([
          Permission.DockAndYard,
          Permission.LocationManagement,
          Permission.Docks,
          Permission.Delete,
        ]),
      },
      [Permission.SecurityCheck]: {
        [Permission.Self]: joinByDelimiter<Permission>([
          Permission.DockAndYard,
          Permission.LocationManagement,
          Permission.SecurityCheck,
        ]),
      },
      [Permission.LoanerPhone]: {
        [Permission.Self]: joinByDelimiter<Permission>([
          Permission.DockAndYard,
          Permission.LocationManagement,
          Permission.LoanerPhone,
        ]),
      },
      [Permission.Drivers]: {
        [Permission.Self]: joinByDelimiter<Permission>([
          Permission.DockAndYard,
          Permission.LocationManagement,
          Permission.Drivers,
        ]),
      },
      [Permission.TruckMapping]: {
        [Permission.Self]: joinByDelimiter<Permission>([
          Permission.DockAndYard,
          Permission.LocationManagement,
          Permission.TruckMapping,
        ]),
      },
      [Permission.ParkingSpots]: {
        [Permission.Self]: joinByDelimiter<Permission>([
          Permission.DockAndYard,
          Permission.LocationManagement,
          Permission.ParkingSpots,
        ]),
        [Permission.Create]: joinByDelimiter<Permission>([
          Permission.DockAndYard,
          Permission.LocationManagement,
          Permission.ParkingSpots,
          Permission.Create,
        ]),
        [Permission.Read]: joinByDelimiter<Permission>([
          Permission.DockAndYard,
          Permission.LocationManagement,
          Permission.ParkingSpots,
          Permission.Read,
        ]),
        [Permission.Update]: joinByDelimiter<Permission>([
          Permission.DockAndYard,
          Permission.LocationManagement,
          Permission.ParkingSpots,
          Permission.Update,
        ]),
        [Permission.Delete]: joinByDelimiter<Permission>([
          Permission.DockAndYard,
          Permission.LocationManagement,
          Permission.ParkingSpots,
          Permission.Delete,
        ]),
      },
    },
    [Permission.AllocationViews]: {
      [Permission.Self]: joinByDelimiter<Permission>([Permission.DockAndYard, Permission.AllocationViews]),
      [Permission.AllocationViewProfiles]: {
        [Permission.Self]: joinByDelimiter<Permission>([
          Permission.DockAndYard,
          Permission.AllocationViews,
          Permission.AllocationViewProfiles,
        ]),
        [Permission.DriverViewSettings]: {
          [Permission.Self]: joinByDelimiter<Permission>([
            Permission.DockAndYard,
            Permission.AllocationViews,
            Permission.AllocationViewProfiles,
            Permission.DriverViewSettings,
          ]),
        },
        [Permission.CalendarViewSettings]: {
          [Permission.Self]: joinByDelimiter<Permission>([
            Permission.DockAndYard,
            Permission.AllocationViews,
            Permission.AllocationViewProfiles,
            Permission.CalendarViewSettings,
          ]),
        },
        [Permission.TerminalViewSettings]: {
          [Permission.Self]: joinByDelimiter<Permission>([
            Permission.DockAndYard,
            Permission.AllocationViews,
            Permission.AllocationViewProfiles,
            Permission.TerminalViewSettings,
          ]),
        },
      },
      [Permission.TerminalView]: {
        [Permission.Self]: joinByDelimiter<Permission>([
          Permission.DockAndYard,
          Permission.AllocationViews,
          Permission.TerminalView,
        ]),
      },
      [Permission.CalendarView]: {
        [Permission.Self]: joinByDelimiter<Permission>([
          Permission.DockAndYard,
          Permission.AllocationViews,
          Permission.CalendarView,
        ]),
      },
      [Permission.DriverView]: {
        [Permission.Self]: joinByDelimiter<Permission>([
          Permission.DockAndYard,
          Permission.AllocationViews,
          Permission.DriverView,
        ]),
      },
      [Permission.UliOverview]: {
        [Permission.Self]: joinByDelimiter<Permission>([
          Permission.DockAndYard,
          Permission.AllocationViews,
          Permission.UliOverview,
        ]),
        [Permission.Read]: joinByDelimiter<Permission>([
          Permission.DockAndYard,
          Permission.AllocationViews,
          Permission.UliOverview,
          Permission.Read,
        ]),
        [Permission.UliDetails]: {
          [Permission.Self]: joinByDelimiter<Permission>([
            Permission.DockAndYard,
            Permission.AllocationViews,
            Permission.UliOverview,
            Permission.UliDetails,
          ]),
          [Permission.Read]: joinByDelimiter<Permission>([
            Permission.DockAndYard,
            Permission.AllocationViews,
            Permission.UliOverview,
            Permission.UliDetails,
            Permission.Read,
          ]),
          [Permission.Update]: joinByDelimiter<Permission>([
            Permission.DockAndYard,
            Permission.AllocationViews,
            Permission.UliOverview,
            Permission.UliDetails,
            Permission.Update,
          ]),
        },
        [Permission.TruckDetails]: {
          [Permission.Self]: joinByDelimiter<Permission>([
            Permission.DockAndYard,
            Permission.AllocationViews,
            Permission.UliOverview,
            Permission.TruckDetails,
          ]),
          [Permission.Read]: joinByDelimiter<Permission>([
            Permission.DockAndYard,
            Permission.AllocationViews,
            Permission.UliOverview,
            Permission.TruckDetails,
            Permission.Read,
          ]),
          [Permission.Update]: joinByDelimiter<Permission>([
            Permission.DockAndYard,
            Permission.AllocationViews,
            Permission.UliOverview,
            Permission.TruckDetails,
            Permission.Update,
          ]),
        },
      },
    },
    [Permission.DaliView]: {
      [Permission.Self]: joinByDelimiter<Permission>([Permission.DockAndYard, Permission.DaliView]),
    },
  },
  [Permission.Administration]: {
    [Permission.Self]: Permission.Administration,
    [Permission.UserManagement]: {
      [Permission.Self]: joinByDelimiter<Permission>([Permission.Administration, Permission.UserManagement]),
      [Permission.Drivers]: {
        [Permission.Self]: joinByDelimiter<Permission>([
          Permission.Administration,
          Permission.UserManagement,
          Permission.Drivers,
        ]),
      },
      [Permission.Users]: {
        [Permission.Self]: joinByDelimiter<Permission>([
          Permission.Administration,
          Permission.UserManagement,
          Permission.Users,
          Permission.Create,
        ]),
        [Permission.Create]: joinByDelimiter<Permission>([
          Permission.Administration,
          Permission.UserManagement,
          Permission.Users,
          Permission.Create,
        ]),
        [Permission.Read]: joinByDelimiter<Permission>([
          Permission.Administration,
          Permission.UserManagement,
          Permission.Users,
          Permission.Read,
        ]),
        [Permission.Update]: joinByDelimiter<Permission>([
          Permission.Administration,
          Permission.UserManagement,
          Permission.Users,
          Permission.Update,
        ]),
        [Permission.Delete]: joinByDelimiter<Permission>([
          Permission.Administration,
          Permission.UserManagement,
          Permission.Users,
          Permission.Delete,
        ]),
        [Permission.Approve]: joinByDelimiter<Permission>([
          Permission.Administration,
          Permission.UserManagement,
          Permission.Users,
          Permission.Approve,
        ]),
      },
    },
    [Permission.PartnerManagement]: {
      [Permission.Self]: joinByDelimiter<Permission>([Permission.Administration, Permission.PartnerManagement]),
      [Permission.Create]: joinByDelimiter<Permission>([
        Permission.Administration,
        Permission.PartnerManagement,
        Permission.Create,
      ]),
      [Permission.Read]: joinByDelimiter<Permission>([
        Permission.Administration,
        Permission.PartnerManagement,
        Permission.Read,
      ]),
      [Permission.Update]: joinByDelimiter<Permission>([
        Permission.Administration,
        Permission.PartnerManagement,
        Permission.Update,
      ]),
      [Permission.Delete]: joinByDelimiter<Permission>([
        Permission.Administration,
        Permission.PartnerManagement,
        Permission.Delete,
      ]),
      [Permission.Approve]: joinByDelimiter<Permission>([
        Permission.Administration,
        Permission.PartnerManagement,
        Permission.Approve,
      ]),
    },
    [Permission.PlantManagement]: {
      [Permission.Self]: joinByDelimiter<Permission>([Permission.Administration, Permission.PlantManagement]),
      [Permission.Create]: joinByDelimiter<Permission>([
        Permission.Administration,
        Permission.PlantManagement,
        Permission.Create,
      ]),
      [Permission.Read]: joinByDelimiter<Permission>([
        Permission.Administration,
        Permission.PlantManagement,
        Permission.Read,
      ]),
      [Permission.Update]: joinByDelimiter<Permission>([
        Permission.Administration,
        Permission.PlantManagement,
        Permission.Update,
      ]),
      [Permission.Delete]: joinByDelimiter<Permission>([
        Permission.Administration,
        Permission.PlantManagement,
        Permission.Delete,
      ]),
    },
    [Permission.AddressManagement]: {
      [Permission.Self]: joinByDelimiter<Permission>([Permission.Administration, Permission.AddressManagement]),
      [Permission.Create]: joinByDelimiter<Permission>([
        Permission.Administration,
        Permission.AddressManagement,
        Permission.Create,
      ]),
      [Permission.Read]: joinByDelimiter<Permission>([
        Permission.Administration,
        Permission.AddressManagement,
        Permission.Read,
      ]),
      [Permission.Update]: joinByDelimiter<Permission>([
        Permission.Administration,
        Permission.AddressManagement,
        Permission.Update,
      ]),
      [Permission.Delete]: joinByDelimiter<Permission>([
        Permission.Administration,
        Permission.AddressManagement,
        Permission.Delete,
      ]),
      [Permission.Export]: joinByDelimiter<Permission>([
        Permission.Administration,
        Permission.AddressManagement,
        Permission.Export,
      ]),
      [Permission.GateAddressIdManagement]: {
        [Permission.Create]: joinByDelimiter<Permission>([
          Permission.Administration,
          Permission.AddressManagement,
          Permission.GateAddressIdManagement,
          Permission.Create,
        ]),
        [Permission.Read]: joinByDelimiter<Permission>([
          Permission.Administration,
          Permission.AddressManagement,
          Permission.GateAddressIdManagement,
          Permission.Read,
        ]),
      },
    },
    [Permission.ApiConfiguration]: {
      [Permission.Self]: joinByDelimiter<Permission>([Permission.Administration, Permission.ApiConfiguration]),
    },
    [Permission.Organizations]: {
      [Permission.Self]: joinByDelimiter<Permission>([Permission.Administration, Permission.Organizations]),
    },
    [Permission.DataProtectionNotice]: {
      [Permission.Self]: joinByDelimiter<Permission>([Permission.Administration, Permission.DataProtectionNotice]),
    },
    [Permission.EndUserLicenseAgreement]: {
      [Permission.Self]: joinByDelimiter<Permission>([Permission.Administration, Permission.EndUserLicenseAgreement]),
    },
    [Permission.Newsletters]: {
      [Permission.Self]: joinByDelimiter<Permission>([Permission.Administration, Permission.Newsletters]),
      [Permission.Create]: joinByDelimiter<Permission>([
        Permission.Administration,
        Permission.Newsletters,
        Permission.Create,
      ]),
      [Permission.Read]: joinByDelimiter<Permission>([
        Permission.Administration,
        Permission.Newsletters,
        Permission.Read,
      ]),
      [Permission.Update]: joinByDelimiter<Permission>([
        Permission.Administration,
        Permission.Newsletters,
        Permission.Update,
      ]),
      [Permission.Delete]: joinByDelimiter<Permission>([
        Permission.Administration,
        Permission.Newsletters,
        Permission.Delete,
      ]),
    },
    [Permission.SapEtaUp]: {
      [Permission.Self]: joinByDelimiter<Permission>([Permission.Administration, Permission.SapEtaUp]),
    },
    [Permission.ContainerManualUpload]: {
      [Permission.Self]: joinByDelimiter<Permission>([Permission.Administration, Permission.ContainerManualUpload]),
    },
  },
  [Permission.Reports]: {
    [Permission.Self]: Permission.Reports,
    [Permission.UserDataChanges]: {
      [Permission.Self]: joinByDelimiter<Permission>([Permission.Reports, Permission.UserDataChanges]),
    },
    [Permission.MasterData]: {
      [Permission.Self]: joinByDelimiter<Permission>([Permission.Reports, Permission.MasterData]),
    },
  },
  [Permission.TrackAndTrace]: {
    [Permission.Self]: Permission.TrackAndTrace,
    [Permission.AutomotiveAftermarket]: {
      [Permission.Self]: joinByDelimiter<Permission>([Permission.TrackAndTrace, Permission.AutomotiveAftermarket]),
      [Permission.DeliveryNoteCard]: {
        [Permission.Self]: joinByDelimiter<Permission>([
          Permission.TrackAndTrace,
          Permission.AutomotiveAftermarket,
          Permission.DeliveryNoteCard,
        ]),
        [Permission.PickUpPoint]: joinByDelimiter<Permission>([
          Permission.TrackAndTrace,
          Permission.AutomotiveAftermarket,
          Permission.DeliveryNoteCard,
          Permission.PickUpPoint,
        ]),
        [Permission.Shipper]: joinByDelimiter<Permission>([
          Permission.TrackAndTrace,
          Permission.AutomotiveAftermarket,
          Permission.DeliveryNoteCard,
          Permission.Shipper,
        ]),
      },
      [Permission.DeliveryNote]: {
        [Permission.PodManagement]: {
          [Permission.Self]: joinByDelimiter<Permission>([
            Permission.TrackAndTrace,
            Permission.AutomotiveAftermarket,
            Permission.DeliveryNote,
            Permission.PodManagement,
          ]),
          [Permission.Create]: joinByDelimiter<Permission>([
            Permission.TrackAndTrace,
            Permission.AutomotiveAftermarket,
            Permission.DeliveryNote,
            Permission.PodManagement,
            Permission.Create,
          ]),
          [Permission.Read]: joinByDelimiter<Permission>([
            Permission.TrackAndTrace,
            Permission.AutomotiveAftermarket,
            Permission.DeliveryNote,
            Permission.PodManagement,
            Permission.Read,
          ]),
          [Permission.Update]: joinByDelimiter<Permission>([
            Permission.TrackAndTrace,
            Permission.AutomotiveAftermarket,
            Permission.DeliveryNote,
            Permission.PodManagement,
            Permission.Update,
          ]),
          [Permission.Delete]: joinByDelimiter<Permission>([
            Permission.TrackAndTrace,
            Permission.AutomotiveAftermarket,
            Permission.DeliveryNote,
            Permission.PodManagement,
            Permission.Delete,
          ]),
        },
      },
      [Permission.HandlingUnitCard]: {
        [Permission.Self]: joinByDelimiter<Permission>([
          Permission.TrackAndTrace,
          Permission.AutomotiveAftermarket,
          Permission.HandlingUnitCard,
        ]),
        [Permission.PickUpPoint]: joinByDelimiter<Permission>([
          Permission.TrackAndTrace,
          Permission.AutomotiveAftermarket,
          Permission.HandlingUnitCard,
          Permission.PickUpPoint,
        ]),
        [Permission.Shipper]: joinByDelimiter<Permission>([
          Permission.TrackAndTrace,
          Permission.AutomotiveAftermarket,
          Permission.HandlingUnitCard,
          Permission.Shipper,
        ]),
      },
      [Permission.TileType]: {
        [Permission.Self]: joinByDelimiter<Permission>([
          Permission.TrackAndTrace,
          Permission.AutomotiveAftermarket,
          Permission.TileType,
        ]),
        [Permission.Load]: joinByDelimiter<Permission>([
          Permission.TrackAndTrace,
          Permission.AutomotiveAftermarket,
          Permission.TileType,
          Permission.Load,
        ]),
        [Permission.TransportOrder]: joinByDelimiter<Permission>([
          Permission.TrackAndTrace,
          Permission.AutomotiveAftermarket,
          Permission.TileType,
          Permission.TransportOrder,
        ]),
        [Permission.DeliveryNote]: joinByDelimiter<Permission>([
          Permission.TrackAndTrace,
          Permission.AutomotiveAftermarket,
          Permission.TileType,
          Permission.DeliveryNote,
        ]),
        [Permission.HandlingUnit]: joinByDelimiter<Permission>([
          Permission.TrackAndTrace,
          Permission.AutomotiveAftermarket,
          Permission.TileType,
          Permission.HandlingUnit,
        ]),
      },
    },
    [Permission.TmSc1Sc4]: {
      [Permission.Self]: joinByDelimiter<Permission>([Permission.TrackAndTrace, Permission.TmSc1Sc4]),
      [Permission.TpComments]: {
        [Permission.Self]: joinByDelimiter<Permission>([
          Permission.TrackAndTrace,
          Permission.TmSc1Sc4,
          Permission.TpComments,
        ]),
        [Permission.Read]: joinByDelimiter<Permission>([
          Permission.TrackAndTrace,
          Permission.TmSc1Sc4,
          Permission.TpComments,
          Permission.Read,
        ]),
        [Permission.Create]: joinByDelimiter<Permission>([
          Permission.TrackAndTrace,
          Permission.TmSc1Sc4,
          Permission.TpComments,
          Permission.Create,
        ]),
      },
      [Permission.TpCustoms]: {
        [Permission.Self]: joinByDelimiter<Permission>([
          Permission.TrackAndTrace,
          Permission.TmSc1Sc4,
          Permission.TpCustoms,
        ]),
        [Permission.Read]: joinByDelimiter<Permission>([
          Permission.TrackAndTrace,
          Permission.TmSc1Sc4,
          Permission.TpCustoms,
          Permission.Read,
        ]),
      },
    },
    [Permission.CanaryTransportLevel]: {
      [Permission.Self]: joinByDelimiter<Permission>([Permission.TrackAndTrace, Permission.CanaryTransportLevel]),
      [Permission.ShipmentCustoms]: {
        [Permission.Self]: joinByDelimiter<Permission>([
          Permission.TrackAndTrace,
          Permission.CanaryTransportLevel,
          Permission.ShipmentCustoms,
        ]),
        [Permission.Read]: joinByDelimiter<Permission>([
          Permission.TrackAndTrace,
          Permission.CanaryTransportLevel,
          Permission.ShipmentCustoms,
          Permission.Read,
        ]),
      },
      [Permission.ShipmentComments]: {
        [Permission.Self]: joinByDelimiter<Permission>([
          Permission.TrackAndTrace,
          Permission.CanaryTransportLevel,
          Permission.ShipmentComments,
        ]),
        [Permission.Read]: joinByDelimiter<Permission>([
          Permission.TrackAndTrace,
          Permission.CanaryTransportLevel,
          Permission.ShipmentComments,
          Permission.Read,
        ]),
        [Permission.Create]: joinByDelimiter<Permission>([
          Permission.TrackAndTrace,
          Permission.CanaryTransportLevel,
          Permission.ShipmentComments,
          Permission.Create,
        ]),
      },
    },
    [Permission.TransportContent]: {
      [Permission.Self]: joinByDelimiter<Permission>([Permission.TrackAndTrace, Permission.TransportContent]),
    },
  },
  [Permission.Exports]: {
    [Permission.Self]: Permission.Exports,
    [Permission.TTExports]: {
      [Permission.Self]: joinByDelimiter<Permission>([Permission.Exports, Permission.TTExports]),
      [Permission.ShipmentExport]: {
        [Permission.Self]: joinByDelimiter<Permission>([
          Permission.Exports,
          Permission.TTExports,
          Permission.ShipmentExport,
        ]),
      },
    },
    [Permission.DataQualityExports]: {
      [Permission.Self]: joinByDelimiter<Permission>([Permission.Exports, Permission.DataQualityExports]),
    },
    [Permission.AdminExports]: {
      [Permission.Self]: joinByDelimiter<Permission>([Permission.Exports, Permission.AdminExports]),
      [Permission.AddressExport]: {
        [Permission.Self]: joinByDelimiter<Permission>([
          Permission.Exports,
          Permission.AdminExports,
          Permission.AddressExport,
        ]),
      },
      [Permission.PartnerExports]: {
        [Permission.Self]: joinByDelimiter<Permission>([
          Permission.Exports,
          Permission.AdminExports,
          Permission.PartnerExports,
        ]),
      },
      [Permission.UserExports]: {
        [Permission.Self]: joinByDelimiter<Permission>([
          Permission.Exports,
          Permission.AdminExports,
          Permission.UserExports,
        ]),
      },
    },
  },
  [Permission.DataQuality]: {
    [Permission.Self]: joinByDelimiter([Permission.DataQuality, Permission.FailedSc4IFTSTA]),
    [Permission.FailedSc4IFTSTA]: {
      [Permission.Self]: joinByDelimiter([Permission.DataQuality, Permission.FailedSc4IFTSTA]),
      [Permission.Read]: Permission.FailedSc4IFTSTA,
    },
  },
  [Permission.SupportInfo]: {
    [Permission.Self]: Permission.SupportInfo,
  },
};
